import React, { Fragment, useState, useEffect, FC } from 'react';

import PropTypes from 'prop-types';

import { getAccHostname } from '@adsk/acs-sc-web-platform-api';
import { useAlloyFeatureFlag } from '@adsk/alloy-provider';
import EmptyState from '@adsk/alloy-react-empty-state';
import Illustration from '@adsk/alloy-react-illustration';
import { useLocale } from '@adsk/alloy-react-locale';
import { SimpleModal } from '@adsk/alloy-react-modal';

import { CURRENT_YEAR_PLACEHOLDER, INTRODUCTION } from '../consts';
import { licenseDataShape } from '../helpers/propTypes';
import { Collection, LicenseDataType } from '../helpers/types';
import AutomaticAboutBoxContent from './AutomaticAboutBoxContent';
import Line from './Line';
import ManualAboutBoxContent from './ManualAboutBoxContent';

type AboutBoxModalProps = {
  show: boolean;
  onClose: () => void;
  licenseData?: LicenseDataType | Collection[] | undefined;
  unsafeForceManualLicenseView?: boolean;
};

const AboutBoxModal: FC<AboutBoxModalProps> = ({
  show,
  licenseData,
  unsafeForceManualLicenseView,
  ...props
}) => {
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
  const [data, setData] = useState<LicenseDataType | Collection[] | undefined>(
    licenseData
  );
  const [error, setError] = useState(false);
  const linkRegex = /\<a(.*?)\/a>/g;

  const { value: showAutomaticLicenseCollection, loading } =
    useAlloyFeatureFlag('show-automatic-license-collection');

  useEffect(() => {
    let cancel = false;

    if (show && !loading && !licenseData) {
      const licensesFile = showAutomaticLicenseCollection
        ? 'assets/license-manager/licenseCollections.json'
        : 'licenses.json';

      fetch(`${getAccHostname()}/${licensesFile}`)
        .then((response) => {
          if (response.ok) {
            return response.json();
          } else {
            throw new Error('License failed to load.');
          }
        })
        .then((newData) => {
          if (!cancel) {
            setData(newData);
          }
        })
        .catch(() => {
          if (!cancel) {
            setError(true);
          }
        });
    }

    return () => {
      cancel = true;
    };
  }, [show, loading, licenseData, showAutomaticLicenseCollection]);

  const shouldShowAutomaticLicenseCollectionView =
    !loading &&
    !unsafeForceManualLicenseView &&
    data &&
    showAutomaticLicenseCollection;

  const shouldShowManualLicenseCollectionView =
    !loading &&
    data &&
    (unsafeForceManualLicenseView || !showAutomaticLicenseCollection);

  return (
    <SimpleModal
      title={T.translate('TRADEMARKS_CREDITS_AND_ATTRIBUTIONS') as string}
      modalBodyProps={{
        style: {
          overflow: 'hidden',
          overflowY: 'auto',
          ...(error && {
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
          }),
        },
      }}
      show={show}
      {...props}
    >
      {!loading &&
        data &&
        INTRODUCTION.map((line) => {
          const textArr = line.text
            .replace(
              CURRENT_YEAR_PLACEHOLDER,
              new Date().getFullYear().toString()
            )
            .replace(linkRegex, '-')
            .split('-');
          const links =
            line.text.match(linkRegex) &&
            line.text.match(linkRegex)?.map((link) => ({
              href: link.match(/href="(.*)">/)?.[1],
              text: link.match(/>(.*)</)?.[1],
            }));

          return (
            <Line key={line.text}>
              {textArr.map((text, i) => (
                <Fragment key={text}>
                  <span>{text}</span>
                  {links && links[i] && (
                    <a href={links[i].href}>{links[i].text}</a>
                  )}
                </Fragment>
              ))}
            </Line>
          );
        })}
      {shouldShowAutomaticLicenseCollectionView ? (
        <AutomaticAboutBoxContent collections={data as Collection[]} />
      ) : null}
      {shouldShowManualLicenseCollectionView ? (
        <ManualAboutBoxContent data={data as LicenseDataType} />
      ) : null}
      {!data && !loading && error && (
        <EmptyState
          title={T.translate('ERROR_LOADING_LICENSES')}
          illustrationType={Illustration.TYPES.FOLDER_EMPTY_GREY}
          description={T.translate('ERROR_LOADING_THIRD_PARTY')}
          style={{ marginTop: -12 }} // Visually center error block
        />
      )}
    </SimpleModal>
  );
};

AboutBoxModal.displayName = 'AboutBoxModal';

AboutBoxModal.propTypes = {
  /** Whether to show the modal or not */
  show: PropTypes.bool.isRequired,
  /** A callback function for when trying to close the modal */
  onClose: PropTypes.func.isRequired,
  /** Use alternative license data (for demo purposes), this data is fetched by default */
  licenseData: licenseDataShape,
  /** To be used in story only and to be deprecated after `show-automatic-license-collection` FF is removed */
  unsafeForceManualLicenseView: PropTypes.bool,
};

export default AboutBoxModal;
